import { FC, useState } from 'react';
import { Box, Chip, Divider, Grid, IconButton, Skeleton, Tooltip, Typography } from '@mui/material';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import TuneIcon from '@mui/icons-material/Tune';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { DateTime } from 'luxon';
import { getSavedUrl } from 'redux/reducers/auth/selectors';
import { PATHS } from 'routes/constants';
import { EOrderStatusCode, EOrderStatusName } from 'redux/reducers/orderManagement/constants';
import { OrderError } from '../Order/OrderError';
import { TOrderDetailProps } from './types';
import { OrderSplitsCard } from './OrderSplitsCard';
import { DeliveryInformationCard } from './DeliveryInformationCard';
import { AmountBreakdownCard } from './AmountBreakdownCard';
import { EMManagementIdHeader } from '../../EMManagement/EMManagementId/EMManagementIdHeader';
import { OrderReturns } from './OrderReturns';
import { OrderDiscounts } from './OrderDiscounts';
import { EasScreenshotFrame } from '../../../ui/EasScreenshotFrame';
import { ErrorCorrection } from './ErrorCorrection';
import { OrderVatCorrections } from './OrderVatCorrections';
import { EasTableValue } from '../../../ui/EasTable/EasTableRow/EasTableValue';
import { ETableStructureType } from '../../../ui/EasTable/types';
import { tableStructure } from '../tableStructure';
import { COLORS } from '../../../services/constants/colors';
import { SwissDeclarations } from './SwissDeclarations';
import { WidgetsVisibilityDialog } from './WidgetsVisibilityDialog';
import {
	DEFAULT_WIDGETS_VISIBILITY,
	EOrderDetailWidget,
	TOrderDetailWidgetsVisibility,
	WIDGETS_VISIBILITY_STORAGE_KEY,
} from './WidgetsVisibilityDialog/constants';

const getStoredWidgetsVisibility = (): TOrderDetailWidgetsVisibility => {
	try {
		const value = window.localStorage.getItem(WIDGETS_VISIBILITY_STORAGE_KEY);
		return value
			? { ...DEFAULT_WIDGETS_VISIBILITY, ...JSON.parse(value) }
			: DEFAULT_WIDGETS_VISIBILITY;
	} catch (err) {
		return DEFAULT_WIDGETS_VISIBILITY;
	}
};

const ORDER_STATUS_LABELS: Record<string, string> = {
	[EOrderStatusCode.POST_SALE_CONFIRMED]: EOrderStatusName.POST_SALE_CONFIRMED,
	[EOrderStatusCode.POST_SALE_SHIPPED]: EOrderStatusName.POST_SALE_SHIPPED,
	[EOrderStatusCode.ACCEPTED]: EOrderStatusName.ACCEPTED,
	[EOrderStatusCode.VALIDATION_FAILED]: EOrderStatusName.VALIDATION_FAILED,
	[EOrderStatusCode.FC_CONFIRMED]: EOrderStatusName.FC_CONFIRMED,
	[EOrderStatusCode.FC_REFUSED]: EOrderStatusName.FC_REFUSED,
	[EOrderStatusCode.PAYMENT_VERIFIED]: EOrderStatusName.PAYMENT_VERIFIED,
	[EOrderStatusCode.PAYMENT_REJECTED]: EOrderStatusName.PAYMENT_REJECTED,
};

const ORDER_STATUS_COLORS: Record<string, string> = {
	[EOrderStatusCode.POST_SALE_CONFIRMED]: 'info',
	[EOrderStatusCode.POST_SALE_SHIPPED]: 'info',
	[EOrderStatusCode.ACCEPTED]: 'success',
	[EOrderStatusCode.VALIDATION_FAILED]: 'error',
	[EOrderStatusCode.FC_CONFIRMED]: 'success',
	[EOrderStatusCode.FC_REFUSED]: 'error',
	[EOrderStatusCode.PAYMENT_VERIFIED]: 'success',
	[EOrderStatusCode.PAYMENT_REJECTED]: 'error',
};

export const OrderDetail: FC<TOrderDetailProps> = ({
	order,
	loading,
	orderSplits,
	orderSplitsLoading,
	// orderItems,
	// orderItemsLoading,
	splitItemsMap,
	splitItemsLoadingMap,
}) => {
	const navigate = useNavigate();
	const savedUrl = useSelector(getSavedUrl);
	const [widgetsDialogOpen, setWidgetsDialogOpen] = useState(false);
	const [widgetsVisibility, setWidgetsVisibility] = useState<TOrderDetailWidgetsVisibility>(
		getStoredWidgetsVisibility,
	);

	const handleWidgetsVisibilityChange = (visibility: TOrderDetailWidgetsVisibility) => {
		setWidgetsVisibility(visibility);
		try {
			window.localStorage.setItem(WIDGETS_VISIBILITY_STORAGE_KEY, JSON.stringify(visibility));
		} catch (err) {
			console.log(err);
		}
	};

	const handleReplayHistory = () => {
		if (savedUrl) {
			if (savedUrl.includes(PATHS.ORDER_MANAGEMENT_PAGE)) navigate(savedUrl);
			else navigate(PATHS.ORDER_MANAGEMENT_PAGE);
		} else {
			navigate(PATHS.ORDER_MANAGEMENT_PAGE);
		}
	};

	if (loading) {
		return (
			<Box sx={{ padding: '10px 20px 0 20px' }}>
				<Skeleton variant="rectangular" height="80px" sx={{ borderRadius: '12px', mb: 2 }} />
				<Grid container spacing={2}>
					<Grid item xs={12} md={8}>
						<Skeleton variant="rectangular" height="350px" sx={{ borderRadius: '12px', mb: 2 }} />
					</Grid>
					<Grid item xs={12} md={4}>
						<Skeleton variant="rectangular" height="300px" sx={{ borderRadius: '12px', mb: 2 }} />
						<Skeleton variant="rectangular" height="300px" sx={{ borderRadius: '12px' }} />
					</Grid>
				</Grid>
			</Box>
		);
	}

	if (!order) {
		return (
			<OrderError
				text="Order not found"
				linkText="Go to the Order Management list"
				link={PATHS.ORDER_MANAGEMENT_PAGE}
			/>
		);
	}

	const currency = order.payment_currency_code ?? 'EUR';
	// const schemes = order.schema_list
	// 	? order.schema_list
	// 			.replaceAll('{', '')
	// 			.replaceAll('}', '')
	// 			.split(',')
	// 			.map((s) => s.trim())
	// 			.filter(Boolean)
	// 	: [];

	return (
		<EasScreenshotFrame
			id="order-detail"
			title={`Order_${order.id}_Detail_${DateTime.fromISO(order.timestamp_utc).toFormat('yyyy-MM-dd_HH-mm-ss')}`}
			button={true}
			frame={true}
		>
			<Box sx={{ padding: '0 20px 0 20px', maxWidth: '100vw', overflowX: 'auto' }}>
				<Box sx={{ minWidth: '660px' }}>
					{/* Header */}
					<EMManagementIdHeader savedUrl={savedUrl} emIdProp={order.em_id} showBackButton={false} />
					<Divider sx={{ my: 1, borderColor: COLORS.PRIMARY }} />
					<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
						<Box sx={{ mb: 1 }}>
							<Box sx={{ display: 'flex', justifyContent: 'start', alignItems: 'center', gap: 1 }}>
								<Tooltip title="Back to the Order Management list">
									<IconButton onClick={handleReplayHistory} color="primary" sx={{ p: 0 }}>
										<KeyboardBackspaceIcon />
									</IconButton>
								</Tooltip>
								<Typography variant="h5" color="primary">
									{order.external_order_id}
								</Typography>
								<EasTableValue
									value={`EAS Order Id: ${order.id}`}
									type={ETableStructureType.STATUS}
									size="m"
									tooltip="EAS Order Id"
									chip
								/>

								{order.order_status && (
									// <Chip
									// 	label={ORDER_STATUS_LABELS[order.order_status] ?? order.order_status}
									// 	size="small"
									// 	sx={{
									// 		p: '0 5px',
									// 		fontSize: '.7rem',
									// 		height: '20px',
									// 		backgroundColor: `${ORDER_STATUS_COLORS[order.order_status] ?? 'primary'}.light`,
									// 		color: '#000000a0',
									// 	}}
									// />
									<EasTableValue
										value={order.order_status}
										type={ETableStructureType.STATUS}
										size="m"
										tooltip="Order Status"
										chip
										tableStructureItem={tableStructure.find(
											(item) => item.selector === 'order_status',
										)}
									/>
								)}
								{order.sale_type && (
									<EasTableValue
										value={order.sale_type}
										type={ETableStructureType.STATUS}
										size="m"
										tooltip="Order Type"
										chip
										tableStructureItem={tableStructure.find(
											(item) => item.selector === 'sale_type',
										)}
									/>
								)}
							</Box>
							<Box
								sx={{
									display: 'flex',
									flexDirection: 'row',
									justifyContent: 'start',
									gap: '20px',
									// ml: '48px',
								}}
							>
								{order.payment_verified_date && (
									<Typography variant="body2" gutterBottom color="GrayText">
										Payment date:{' '}
										{DateTime.fromISO(order.payment_verified_date).toFormat('yyyy-MM-dd, HH:mm')}
									</Typography>
								)}
								{order.sale_date_provided_by_em && (
									<Typography variant="body2" gutterBottom color="GrayText">
										Real Sale date:{' '}
										{DateTime.fromISO(order.sale_date_provided_by_em).toFormat('yyyy-MM-dd, HH:mm')}
									</Typography>
								)}
								{order.post_sale_payment_verified_date && (
									<Typography variant="body2" gutterBottom color="GrayText">
										Post Sale Payment Date:{' '}
										{DateTime.fromISO(order.post_sale_payment_verified_date).toFormat(
											'yyyy-MM-dd, HH:mm',
										)}
									</Typography>
								)}
								{order.timestamp && (
									<Typography variant="body2" gutterBottom color="GrayText">
										Order date:{' '}
										{DateTime.fromISO(order.timestamp_utc).toFormat('yyyy-MM-dd, HH:mm')}
									</Typography>
								)}
							</Box>
						</Box>
						<Tooltip title="Widgets visibility">
							<IconButton onClick={() => setWidgetsDialogOpen(true)} color="primary" sx={{ p: 0 }}>
								<TuneIcon />
							</IconButton>
						</Tooltip>
					</Box>
					<WidgetsVisibilityDialog
						open={widgetsDialogOpen}
						onClose={() => setWidgetsDialogOpen(false)}
						visibility={widgetsVisibility}
						onChange={handleWidgetsVisibilityChange}
					/>

					<Grid container spacing={2}>
						{/* Left column */}
						<Grid item xs={12} sm={12} md={8} lg={8} xl={8} sx={{ pl: '8px !important', gap: 2 }}>
							{widgetsVisibility[EOrderDetailWidget.SPLITS] && (
								<OrderSplitsCard
									splits={orderSplits}
									loading={orderSplitsLoading}
									currency={currency}
									splitItemsMap={splitItemsMap}
									splitItemsLoadingMap={splitItemsLoadingMap}
								/>
							)}
							{widgetsVisibility[EOrderDetailWidget.RETURNS] && (
								<OrderReturns paymentCurrency={currency} />
							)}
							{widgetsVisibility[EOrderDetailWidget.DISCOUNTS] && (
								<OrderDiscounts paymentCurrency={currency} />
							)}
							{widgetsVisibility[EOrderDetailWidget.VAT_CORRECTIONS] && (
								<OrderVatCorrections key={order.id} orderId={order.id} />
							)}
							{widgetsVisibility[EOrderDetailWidget.ERROR_CORRECTION] && (
								<ErrorCorrection orderId={order.id} />
							)}
							{widgetsVisibility[EOrderDetailWidget.SWISS_DECLARATIONS] && (
								<SwissDeclarations orderId={order.id} />
							)}
						</Grid>

						{/* Right column */}
						<Grid item xs={12} sm={12} md={4} lg={4} xl={4} sx={{ pl: '8px !important' }}>
							{widgetsVisibility[EOrderDetailWidget.DELIVERY_INFORMATION] && (
								<DeliveryInformationCard order={order} currency={currency} />
							)}

							{widgetsVisibility[EOrderDetailWidget.AMOUNT_BREAKDOWN] && (
								<AmountBreakdownCard order={order} currency={currency} />
							)}
						</Grid>
					</Grid>
				</Box>
			</Box>
		</EasScreenshotFrame>
	);
};
